import { useRef, useState, useEffect } from 'react';

import { useDrag } from '@use-gesture/react';
import LocomotiveScroll from 'locomotive-scroll';
import { anchorType } from 'react-xarrows';

const SLIDE_WIDTH = 75;

type IntroElementSlide = Readonly<{
  index: number;
  currentSlide: number;
  setCurrentSlide: (index: number) => void;
}>;

export type IntroSlide = Readonly<{
  variant:
    | {
        type: 'STANDARD';
        title: string;
        description: string;
        imagePath: string;
      }
    | {
        type: 'ELEMENT';
        element: ({ index, currentSlide, setCurrentSlide }: IntroElementSlide) => JSX.Element;
      };
  startAnchor: anchorType;
  endAnchor: anchorType;
}>;

export const useIntroSlides = (slides: IntroSlide[]) => {
  const [activeSlide, setActiveSlide] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<LocomotiveScroll | null>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  const scrollToSlide = createScrollToSlide({
    slideRefs,
    scrollRef,
    setActiveSlide,
  });

  const bind = useSwipeHandler({
    slides,
    activeSlide,
    scrollToSlide,
  });

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' && activeSlide < slides.length - 1) {
        scrollToSlide(activeSlide + 1);
      } else if (event.key === 'ArrowLeft' && activeSlide > 0) {
        scrollToSlide(activeSlide - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeSlide, slides.length, scrollToSlide]);

  useEffect(() => {
    initializeScroll({
      containerRef,
      scrollRef,
      setActiveSlide,
    });

    const scrollInstance = scrollRef.current;

    return () => {
      if (scrollInstance) {
        scrollInstance.destroy();
      }
    };
  }, []);

  return {
    activeSlide,
    setActiveSlide,
    containerRef,
    slideRefs,
    bind,
    scrollToSlide,
  };
};

const createScrollToSlide = ({
  slideRefs,
  scrollRef,
  setActiveSlide,
}: {
  slideRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  scrollRef: React.MutableRefObject<LocomotiveScroll | null>;
  setActiveSlide: React.Dispatch<React.SetStateAction<number>>;
}) => {
  return (index: number) => {
    if (!scrollRef.current || !slideRefs.current[index]) {
      return;
    }

    scrollRef.current.scrollTo(slideRefs.current[index], {
      duration: 1000,
      offset: 0,
    });

    setActiveSlide(index);
  };
};

const useSwipeHandler = ({
  slides,
  activeSlide,
  scrollToSlide,
}: {
  slides: IntroSlide[];
  activeSlide: number;
  scrollToSlide: (index: number) => void;
}) => {
  return useDrag(
    ({ swipe: [swipeX], direction: [dirX], velocity, cancel, first }) => {
      if (!first) {
        return;
      }

      cancel();

      if (Number(velocity) < 0.2 && swipeX === 0) {
        return;
      }

      if (dirX === -1 && activeSlide < slides.length - 1) {
        scrollToSlide(activeSlide + 1);
      } else if (dirX === 1 && activeSlide > 0) {
        scrollToSlide(activeSlide - 1);
      }
    },
    {
      swipe: { distance: 50, velocity: 0.2 },
      axis: 'x',
      filterTaps: true,
    },
  );
};

const initializeScroll = ({
  containerRef,
  scrollRef,
  setActiveSlide,
}: {
  containerRef: React.RefObject<HTMLDivElement>;
  scrollRef: React.MutableRefObject<LocomotiveScroll | null>;
  setActiveSlide: React.Dispatch<React.SetStateAction<number>>;
}) => {
  if (!containerRef.current) {
    return;
  }

  // Check if we're on an iPad in landscape mode
  const isIPad =
    /iPad/.test(navigator.userAgent) ||
    (/Macintosh/.test(navigator.userAgent) && 'ontouchend' in document);
  const isLandscape = window.matchMedia('(orientation: landscape)').matches;

  scrollRef.current = new LocomotiveScroll({
    el: containerRef.current,
    smooth: true,
    direction: 'horizontal',
    gestureDirection: 'both',
    multiplier: 0.75,
    tablet: {
      smooth: true,
      direction: 'horizontal',
      gestureDirection: 'horizontal',
      breakpoint: 1024,
    },
    smartphone: {
      smooth: true,
      direction: 'horizontal',
      gestureDirection: 'horizontal',
    },
  });

  // Add resize listener to handle orientation changes
  const handleResize = () => {
    if (scrollRef.current) {
      scrollRef.current.update();
    }
  };

  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', handleResize);

  scrollRef.current.on('scroll', ({ scroll: { x } }) => {
    const viewportWidth = window.innerWidth;
    const slideAdjustment = isIPad && isLandscape ? 0.95 : 1; // Slight adjustment for iPad landscape
    const currentSlide = Math.round(x / (viewportWidth * (SLIDE_WIDTH / 100) * slideAdjustment));

    setActiveSlide(currentSlide);
  });
};
